import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  InputAdornment,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  Button,
  Skeleton,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { searchUsers } from '../endpoints/usersTs';
import { useApiGet } from '../utils/httpClientTs';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { TableVirtuoso } from 'react-virtuoso';
import ChapterSelector from './ChapterSelector';
import { UserSearchRecord } from '../types/users';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '52vw',
  height: '100',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
};

export interface User {
  id: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  imis_id?: string;
  chapter?: string;
  chapter_name?: string;
  current_chapter?: string | null;
  member_status?: string;
  [key: string]: any; // Allow additional properties for flexibility
}

interface BaseUserSearchModalProps {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  chapter?: string | null; // Deprecated - not supported by Go server, kept for backward compatibility
  spd?: string | null;
  activeMembers?: boolean;
  allowSetToMe?: boolean;
  statusFilter?:
    | 'active'
    | 'inactive'
    | 'all-members'
    | 'deceased'
    | 'non-members'
    | 'all';
  enablePagination?: boolean; // Enable pagination for large result sets
}

interface UserSearchModalPropsWithObject extends BaseUserSearchModalProps {
  userObjectRet: true;
  setUser: (user: User) => void;
}

interface UserSearchModalPropsWithString extends BaseUserSearchModalProps {
  userObjectRet?: false;
  setUser: (user: string) => void;
}

type UserSearchModalProps =
  | UserSearchModalPropsWithObject
  | UserSearchModalPropsWithString;

const columns = [
  {
    width: 50,
    label: 'Name',
    dataKey: 'full_name',
  },
  {
    width: 50,
    label: 'iMIS ID',
    dataKey: 'imis_id',
  },
  {
    width: 10,
    label: null,
    dataKey: 'info',
    skip: true,
  },
];

const chapterColumns = [
  {
    width: 50,
    label: 'Name',
    dataKey: 'full_name',
  },
  {
    width: 50,
    label: 'iMIS ID',
    dataKey: 'imis_id',
  },
  {
    width: 50,
    label: 'Chapter',
    dataKey: 'chapter',
  },
  {
    width: 10,
    label: null,
    dataKey: 'info',
    skip: true,
  },
];

const VirtuosoTableComponents = {
  Scroller: React.forwardRef<HTMLDivElement, any>((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props: any) => (
    <Table
      size="small"
      {...props}
      sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }}
    />
  ),
  TableHead: React.forwardRef<HTMLTableSectionElement, any>((props, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableRow,
  TableBody: React.forwardRef<HTMLTableSectionElement, any>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

const UserSearchModal: React.FC<UserSearchModalProps> = ({
  modalOpen,
  setUser,
  setModalOpen,
  chapter,
  spd,
  activeMembers = true,
  userObjectRet = false,
  allowSetToMe = true,
  statusFilter = 'all',
  enablePagination = false,
}) => {
  const [searchText, setSearchText] = useState('');
  const [delaySearch, setDelaySearch] = useState('');
  const [membersOnly, setMembersOnly] = useState(activeMembers !== false); // Default to true unless explicitly false
  const [status, setStatus] = useState<
    'active' | 'inactive' | 'all-members' | 'deceased' | 'non-members' | 'all'
  >(statusFilter);
  const [selectedChapter, setSelectedChapter] = useState<string | null>(
    chapter || null
  );
  const [page, setPage] = useState(0);
  const [perPage] = useState(50); // Default per Go server

  // Map membersOnly and status to member_status for Go server
  // Note: 'all-members' is not supported by the API, so we map it to 'all' which won't be sent
  const getMemberStatus = ():
    | 'active'
    | 'inactive'
    | 'deceased'
    | 'non-members'
    | 'all' => {
    if (!membersOnly) {
      return 'all'; // Show all users (members and non-members)
    }
    // If membersOnly is true, use the status filter
    if (status === 'all' || status === 'all-members') {
      return 'all'; // Return 'all' which won't be sent to API, effectively showing all members
    }
    return status;
  };

  const { data, isFetching } = useApiGet(
    [
      'user-search',
      searchText,
      membersOnly,
      status,
      selectedChapter,
      spd,
      page,
      perPage,
      enablePagination,
    ],
    () => {
      // Determine which chapter parameter to use
      // If chapter prop is provided, it's a local chapter
      // If spd prop is provided, it's an SPD chapter
      // If ChapterSelector is used (chapter === undefined), selectedChapter type depends on spd prop
      let localChapterId: string | undefined = undefined;
      let spdChapterId: string | undefined = undefined;

      if (chapter !== undefined) {
        // chapter prop is provided, use it for local_chapter_id
        localChapterId = chapter || undefined;
      } else if (selectedChapter) {
        // ChapterSelector is being used
        if (spd) {
          // If spd prop is provided, selectedChapter is an SPD chapter
          spdChapterId = selectedChapter;
        } else {
          // If spd prop is not provided, selectedChapter is a local chapter
          localChapterId = selectedChapter;
        }
      }

      // Also use spd prop directly if provided (for cases where spd is set but no chapter selector)
      if (spd && !spdChapterId) {
        spdChapterId = spd;
      }

      return searchUsers({
        search: searchText || undefined,
        member_status: getMemberStatus(),
        spd_chapter_id: spdChapterId,
        local_chapter_id: localChapterId,
        page: enablePagination ? page : undefined,
        per_page: enablePagination ? perPage : undefined,
      });
    },
    {
      enabled: searchText.length > 2 || enablePagination, // Allow empty search if pagination enabled
      retry: 1,
    }
  );

  // Handle paginated vs non-paginated response
  const users: User[] =
    data?.data?.map((record: UserSearchRecord) => ({
      id: record.id,
      first_name: record.first_name,
      last_name: record.last_name,
      full_name: `${record.first_name} ${record.last_name}`,
      current_chapter: record.current_chapter,
      chapter: record.current_chapter || undefined,
      chapter_name: record.current_chapter || undefined,
      member_status: record.member_status,
    })) || [];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchText(delaySearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  // Reset to defaults when modal opens
  useEffect(() => {
    if (modalOpen) {
      setMembersOnly(activeMembers !== false); // Reset to prop value
      setSelectedChapter(chapter || null);
      setDelaySearch('');
      setSearchText('');
      setPage(0); // Reset pagination
      setStatus(statusFilter); // Reset status filter
    }
  }, [modalOpen, chapter, activeMembers, statusFilter]);

  const handleUserSelected = (user: User) => {
    if (userObjectRet) {
      (setUser as (user: User) => void)(user);
    } else {
      (setUser as (user: string) => void)(user.id);
    }
    setDelaySearch('');
    setSearchText('');
  };

  const getColumns = () => {
    return membersOnly ? chapterColumns : columns;
  };

  const rowContent = (_index: number, row: User) => {
    // Helper to get display value for a column
    const getDisplayValue = (dataKey: string): string => {
      if (dataKey === 'full_name') {
        // Prefer full_name, but fall back to first_name + last_name
        return (
          row.full_name ||
          (row.first_name && row.last_name
            ? `${row.first_name} ${row.last_name}`
            : '') ||
          ''
        );
      }
      return row[dataKey as keyof User] || '';
    };

    return (
      <React.Fragment>
        {getColumns()
          .filter((item) => !item.skip)
          .map((column) => (
            <TableCell
              key={column.dataKey}
              align={'right'}
              onClick={() => handleUserSelected(row)}
            >
              {getDisplayValue(column.dataKey)}
            </TableCell>
          ))}
        <TableCell
          key={'select'}
          align={'right'}
          onClick={() => handleUserSelected(row)}
        >
          <IconButton>
            <TaskAltIcon color="primary" />
          </IconButton>
        </TableCell>
      </React.Fragment>
    );
  };

  const handleCloseModal = () => {
    setDelaySearch('');
    setSearchText('');
    setSelectedChapter(chapter || null); // Reset to initial chapter
    setModalOpen(false);
  };

  const handleSetToMe = () => {
    const userId = localStorage.getItem('user_id');
    if (userId) {
      if (userObjectRet) {
        // When userObjectRet is true, we can't use Set to Me with just an ID
        // This should ideally not be called, but handle gracefully
        console.warn('Set to Me is not supported when userObjectRet is true');
      } else {
        (setUser as (user: string) => void)(userId);
      }
    }
    setDelaySearch('');
    setSearchText('');
  };

  const fixedHeaderContent = () => {
    return (
      <TableRow>
        {getColumns().map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.dataKey === 'action' ? 'left' : 'right'}
            style={{ width: column.width }}
            sx={{
              backgroundColor: 'background.paper',
            }}
          >
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  return (
    <Modal
      open={modalOpen}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Grid container>
          <Grid item xs={12} sx={{ textAlign: 'right' }}>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Grid>
          {allowSetToMe && (
            <Grid
              item
              xs={12}
              paddingTop={2}
              paddingRight={2}
              textAlign={'right'}
            >
              <Button color="primary" size="small" onClick={handleSetToMe}>
                Set to Me
              </Button>
            </Grid>
          )}
        </Grid>

        <Grid container>
          <Grid item xs={12} padding={2}>
            <TextField
              label="Search"
              id="search-field"
              variant="standard"
              size="small"
              fullWidth={true}
              value={delaySearch}
              onChange={(event) => setDelaySearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} padding={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={membersOnly}
                      onChange={() => setMembersOnly(!membersOnly)}
                    />
                  }
                  label="Members Only"
                />
                {membersOnly && (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      id="status-filter"
                      value={status}
                      label="Status"
                      onChange={(e) =>
                        setStatus(
                          e.target.value as
                            | 'active'
                            | 'inactive'
                            | 'all-members'
                            | 'deceased'
                            | 'non-members'
                            | 'all'
                        )
                      }
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="all-members">All Members</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="deceased">Deceased</MenuItem>
                      <MenuItem value="non-members">Non-Members</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
              {chapter === undefined && (
                <Box sx={{ minWidth: 300 }}>
                  <ChapterSelector
                    chapter={selectedChapter}
                    handleChapterSelect={(value) =>
                      setSelectedChapter(value || null)
                    }
                    spd={spd || null}
                    reportId="user-search-modal"
                    label="Filter by Chapter"
                    all={true}
                  />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} padding={2}>
            {isFetching ? (
              <Skeleton variant="rectangular" width={'50vw'} height={'50vh'} />
            ) : (
              <Box sx={{ height: '50vh', width: '50vw' }}>
                {users && users.length > 0 ? (
                  <>
                    <TableVirtuoso
                      data={users}
                      components={VirtuosoTableComponents}
                      fixedHeaderContent={fixedHeaderContent}
                      itemContent={rowContent}
                    />
                    {enablePagination && data?.pagination && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 2,
                          pt: 2,
                          borderTop: '1px solid #e0e0e0',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Showing {users.length} of{' '}
                          {data.pagination.total_count} results
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            disabled={!data.pagination.has_prev}
                            onClick={() => setPage(page - 1)}
                          >
                            Previous
                          </Button>
                          <Typography
                            variant="body2"
                            sx={{ alignSelf: 'center', px: 1 }}
                          >
                            Page {data.pagination.page + 1} of{' '}
                            {data.pagination.total_pages}
                          </Typography>
                          <Button
                            size="small"
                            disabled={!data.pagination.has_next}
                            onClick={() => setPage(page + 1)}
                          >
                            Next
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography
                    variant="h6"
                    component="h2"
                    color={'primary'}
                    paddingBottom={2}
                  >
                    {searchText.length > 2 || enablePagination
                      ? 'No results found'
                      : 'Please enter at least 3 characters'}
                  </Typography>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default UserSearchModal;
